import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import * as recsApi from '../../api/recommendations';
import AdminLayout from './AdminLayout';
import styles from './AdminRecommendations.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const STATUS_LABELS = { draft: 'Черновик', published: 'Опубликован', archived: 'Архив' };

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
};

const EMPTY_FORM = {
  title: '',
  description: '',
  link: '',
  image_alt: '',
  promo_code: '',
  visible_to_paid_only: false,
  status: 'draft',
};

const AdminRecommendations = () => {
  const { token } = useAuth();
  const [recs, setRecs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingRec, setEditingRec] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [formMessage, setFormMessage] = useState({ text: '', type: '' });

  // Изображение
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const fileInputRef = useRef(null);

  // Прикреплённый текстовый файл
  const [attachFile, setAttachFile] = useState(null);
  const [existingFile, setExistingFile] = useState(null); // { name, size, type, url } для редактирования
  const [removeFile, setRemoveFile] = useState(false);
  const attachInputRef = useRef(null);

  const fetchRecs = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await recsApi.getAllRecommendationsAdmin(token);
      setRecs(data.recommendations);
    } catch {
      setError('Ошибка загрузки рекомендаций');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchRecs(); }, [fetchRecs]);

  const openCreate = () => {
    setEditingRec(null);
    setForm(EMPTY_FORM);
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(false);
    setAttachFile(null);
    setExistingFile(null);
    setRemoveFile(false);
    setFormMessage({ text: '', type: '' });
    setShowForm(true);
  };

  const openEdit = (rec) => {
    setEditingRec(rec);
    setForm({
      title: rec.title,
      description: rec.description,
      link: rec.link || '',
      image_alt: rec.image_alt || '',
      promo_code: rec.promo_code || '',
      visible_to_paid_only: rec.visible_to_paid_only,
      status: rec.status,
    });
    setImageFile(null);
    setImagePreview(rec.image_url ? `${API_URL}${rec.image_url}` : null);
    setRemoveImage(false);
    setAttachFile(null);
    setExistingFile(rec.file_url
      ? { name: rec.file_name, size: rec.file_size, type: rec.file_type, url: rec.file_url }
      : null);
    setRemoveFile(false);
    setFormMessage({ text: '', type: '' });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingRec(null);
    setImageFile(null);
    setImagePreview(null);
    setAttachFile(null);
    setExistingFile(null);
    setRemoveFile(false);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setRemoveImage(false);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAttachFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const MAX = 200 * 1024 * 1024;
    if (file.size > MAX) {
      setFormMessage({ text: 'Файл больше 200 МБ — выберите файл поменьше', type: 'error' });
      e.target.value = '';
      return;
    }
    setAttachFile(file);
    setRemoveFile(false);
    setFormMessage({ text: '', type: '' });
  };

  const handleRemoveAttachFile = () => {
    setAttachFile(null);
    setExistingFile(null);
    setRemoveFile(true);
    if (attachInputRef.current) attachInputRef.current.value = '';
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
    return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      setFormMessage({ text: 'Заполните название и описание', type: 'error' });
      return;
    }

    setIsSaving(true);
    setFormMessage({ text: '', type: '' });

    const fd = new FormData();
    fd.append('title', form.title);
    fd.append('description', form.description);
    fd.append('link', form.link);
    fd.append('image_alt', form.image_alt);
    fd.append('promo_code', form.promo_code);
    fd.append('visible_to_paid_only', String(form.visible_to_paid_only));
    fd.append('status', form.status);
    if (imageFile) fd.append('image', imageFile);
    if (removeImage) fd.append('remove_image', 'true');
    if (attachFile) fd.append('file', attachFile);
    if (removeFile) fd.append('remove_file', 'true');

    try {
      if (editingRec) {
        await recsApi.updateRecommendation(token, editingRec.id, fd);
        setFormMessage({ text: 'Рекомендация обновлена', type: 'success' });
      } else {
        await recsApi.createRecommendation(token, fd);
        setFormMessage({ text: 'Рекомендация создана', type: 'success' });
      }
      fetchRecs();
      setTimeout(closeForm, 1000);
    } catch (err) {
      setFormMessage({ text: err.response?.data?.message || 'Ошибка сохранения', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить рекомендацию? Это действие нельзя отменить.')) return;
    try {
      await recsApi.deleteRecommendation(token, id);
      setRecs((prev) => prev.filter((r) => r.id !== id));
    } catch {
      alert('Ошибка при удалении');
    }
  };

  return (
    <AdminLayout>
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>Рекомендации</h1>
          <button className={styles.createBtn} onClick={openCreate}>
            + Добавить рекомендацию
          </button>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {/* Форма создания/редактирования */}
        {showForm && (
          <div className={styles.formWrap}>
            <div className={styles.formHeader}>
              <h2 className={styles.formTitle}>
                {editingRec ? 'Редактировать рекомендацию' : 'Новая рекомендация'}
              </h2>
              <button className={styles.closeBtn} onClick={closeForm}>✕</button>
            </div>

            {formMessage.text && (
              <div className={`${styles.formMessage} ${styles[formMessage.type]}`}>
                {formMessage.text}
              </div>
            )}

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formGrid}>
                {/* Левая колонка */}
                <div className={styles.formCol}>
                  <div className={styles.field}>
                    <label className={styles.label}>Название *</label>
                    <input
                      name="title"
                      value={form.title}
                      onChange={handleFormChange}
                      className={styles.input}
                      placeholder="Название БАДа или рекомендации"
                    />
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label}>Описание *</label>
                    <textarea
                      name="description"
                      value={form.description}
                      onChange={handleFormChange}
                      className={styles.textarea}
                      rows={5}
                      placeholder="Подробное описание рекомендации..."
                    />
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label}>Ссылка (URL)</label>
                    <input
                      name="link"
                      value={form.link}
                      onChange={handleFormChange}
                      className={styles.input}
                      placeholder="https://..."
                    />
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label}>Промокод</label>
                    <input
                      name="promo_code"
                      value={form.promo_code}
                      onChange={handleFormChange}
                      className={styles.input}
                      placeholder="SUMMER25"
                    />
                  </div>
                </div>

                {/* Правая колонка */}
                <div className={styles.formCol}>
                  {/* Изображение */}
                  <div className={styles.field}>
                    <label className={styles.label}>Изображение</label>
                    <div className={styles.imageUpload}>
                      {imagePreview ? (
                        <div className={styles.imagePreviewWrap}>
                          <img src={imagePreview} alt="Preview" className={styles.imagePreview} />
                          <button
                            type="button"
                            className={styles.removeImageBtn}
                            onClick={handleRemoveImage}
                          >
                            ✕ Удалить
                          </button>
                        </div>
                      ) : (
                        <label className={styles.uploadArea} htmlFor="rec-image">
                          <span className={styles.uploadIcon}>📷</span>
                          <span className={styles.uploadText}>Загрузить изображение</span>
                          <span className={styles.uploadHint}>JPEG, PNG, WebP · до 10 МБ</span>
                        </label>
                      )}
                      <input
                        id="rec-image"
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleFileChange}
                        className={styles.fileInput}
                      />
                    </div>
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label}>Alt-текст изображения</label>
                    <input
                      name="image_alt"
                      value={form.image_alt}
                      onChange={handleFormChange}
                      className={styles.input}
                      placeholder="Описание изображения для доступности"
                    />
                  </div>

                  {/* Прикреплённый файл */}
                  <div className={styles.field}>
                    <label className={styles.label}>Прикреплённый файл</label>
                    <div className={styles.imageUpload}>
                      {attachFile ? (
                        <div className={styles.attachInfo}>
                          <div className={styles.attachMeta}>
                            <span className={styles.attachIcon}>📄</span>
                            <div>
                              <div className={styles.attachName}>{attachFile.name}</div>
                              <div className={styles.attachSize}>{formatFileSize(attachFile.size)} · готов к загрузке</div>
                            </div>
                          </div>
                          <button
                            type="button"
                            className={styles.removeImageBtn}
                            onClick={handleRemoveAttachFile}
                          >
                            ✕ Убрать
                          </button>
                        </div>
                      ) : existingFile ? (
                        <div className={styles.attachInfo}>
                          <div className={styles.attachMeta}>
                            <span className={styles.attachIcon}>📄</span>
                            <div>
                              <a
                                href={`${API_URL}${existingFile.url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.attachName}
                              >
                                {existingFile.name}
                              </a>
                              <div className={styles.attachSize}>
                                {formatFileSize(existingFile.size)} · уже загружен
                              </div>
                            </div>
                          </div>
                          <div className={styles.attachActions}>
                            <label className={styles.replaceBtn} htmlFor="rec-attach">
                              Заменить
                            </label>
                            <button
                              type="button"
                              className={styles.removeImageBtn}
                              onClick={handleRemoveAttachFile}
                            >
                              ✕ Удалить
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label className={styles.uploadArea} htmlFor="rec-attach">
                          <span className={styles.uploadIcon}>📎</span>
                          <span className={styles.uploadText}>Прикрепить файл</span>
                          <span className={styles.uploadHint}>PDF, DOC, DOCX, TXT, RTF, MD · до 200 МБ</span>
                        </label>
                      )}
                      <input
                        id="rec-attach"
                        ref={attachInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,.txt,.rtf,.md,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/rtf,text/rtf,text/markdown"
                        onChange={handleAttachFileChange}
                        className={styles.fileInput}
                      />
                    </div>
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label}>Статус</label>
                    <select
                      name="status"
                      value={form.status}
                      onChange={handleFormChange}
                      className={styles.input}
                    >
                      <option value="draft">Черновик</option>
                      <option value="published">Опубликован</option>
                      <option value="archived">Архив</option>
                    </select>
                  </div>

                  <div className={styles.checkboxRow}>
                    <label className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        name="visible_to_paid_only"
                        checked={form.visible_to_paid_only}
                        onChange={handleFormChange}
                      />
                      Только для платных пользователей
                    </label>
                    <p className={styles.hint}>
                      Если включено — бесплатные пользователи видят карточку, но контент размыт
                    </p>
                  </div>
                </div>
              </div>

              <div className={styles.formActions}>
                <button type="submit" className={styles.saveBtn} disabled={isSaving}>
                  {isSaving ? 'Сохранение...' : editingRec ? 'Сохранить' : 'Создать'}
                </button>
                <button type="button" className={styles.cancelBtn} onClick={closeForm}>
                  Отмена
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Таблица */}
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Изображение</th>
                <th className={styles.th}>Название</th>
                <th className={styles.th}>Статус</th>
                <th className={styles.th}>Доступ</th>
                <th className={styles.th}>Создано</th>
                <th className={styles.th}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={6} className={styles.loadingCell}>Загрузка...</td></tr>
              ) : recs.length === 0 ? (
                <tr><td colSpan={6} className={styles.emptyCell}>Рекомендаций пока нет</td></tr>
              ) : (
                recs.map((rec) => (
                  <tr key={rec.id} className={styles.row}>
                    <td className={styles.td}>
                      {rec.image_url ? (
                        <img
                          src={`${API_URL}${rec.image_url}`}
                          alt={rec.image_alt || rec.title}
                          className={styles.thumbImage}
                        />
                      ) : (
                        <div className={styles.thumbPlaceholder}>—</div>
                      )}
                    </td>
                    <td className={styles.td}>
                      <span className={styles.recTitle}>{rec.title}</span>
                      {rec.description && (
                        <span className={styles.recPreview}>
                          {rec.description.slice(0, 60)}{rec.description.length > 60 ? '...' : ''}
                        </span>
                      )}
                    </td>
                    <td className={styles.td}>
                      <span className={`${styles.statusBadge} ${styles[`status_${rec.status}`]}`}>
                        {STATUS_LABELS[rec.status]}
                      </span>
                    </td>
                    <td className={styles.td}>
                      {rec.visible_to_paid_only ? (
                        <span className={styles.paidBadge}>Premium</span>
                      ) : (
                        <span className={styles.freeBadge}>Все</span>
                      )}
                    </td>
                    <td className={styles.td}>{formatDate(rec.created_at)}</td>
                    <td className={styles.td}>
                      <div className={styles.rowActions}>
                        <button className={styles.editBtn} onClick={() => openEdit(rec)}>Изменить</button>
                        <button className={styles.deleteBtn} onClick={() => handleDelete(rec.id)}>Удалить</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminRecommendations;
